"use client";

import { CoverageMap } from "~/components/ui/CoverageMap";
import { useSearchBlockedOutOfArea } from "~/hooks/useSearchBlockedOutOfArea";
import { outOfAreaHeadline, SERVICE_AREA_NAME } from "~/lib/service-area";

/**
 * Shown under the location field when the picked destination is outside the
 * service area, in every search entry point: the landing card, the desktop nav
 * pill, and the mobile sheet. Names the destination's city when Mapbox gave us
 * one, and shows where EZSpot *is* rather than only where it isn't.
 *
 * `onChangeDestination` clears the field and refocuses it, so the only way
 * forward is a different destination. The caller keeps its submit disabled
 * while this is on screen.
 *
 * It also reports the block, since it renders in exactly the cases the gate
 * closes and the three call sites would otherwise repeat the same effect.
 */
export function OutOfAreaNotice({
  city,
  onChangeDestination,
}: {
  city: string | null | undefined;
  onChangeDestination: () => void;
}) {
  useSearchBlockedOutOfArea(city, "search");

  return (
    // `status`, not `alert`: this is the answer to a destination the driver just
    // picked, not an interruption, and it is the only feedback for the submit
    // button going inert. The sibling retrieve-failure line uses `alert` because
    // that one reports something that went wrong.
    <div role="status" className="flex items-center gap-3 pt-3">
      <CoverageMap size={72} className="shrink-0" />
      <div className="min-w-0">
        <p className="text-[14px] font-semibold text-body">
          {outOfAreaHeadline(city)}
        </p>
        <p className="mt-0.5 text-[13px] text-secondary">
          EZSpot parks in {SERVICE_AREA_NAME} only for now.
        </p>
        <button
          type="button"
          onClick={onChangeDestination}
          className="mt-0.5 text-[13px] font-semibold text-accent hover:underline"
        >
          Search {SERVICE_AREA_NAME}
        </button>
      </div>
    </div>
  );
}
